import React from 'react';
import * as Yup from 'yup';
import { ErrorMessage, Field, FieldArray, Form, Formik } from 'formik';
import Typesense from 'typesense';
import { Checkbox, ColorField, DropdownField, JsonField, MultiSelectField, TextAreaField, TextField } from './settings-form/elements';
import { FILTER_KINDS, GENERIC_FILTER_KINDS, ROW_FILTER_KINDS, AXIS_FILTER_KINDS } from './settings-form/filterKinds';
import FacetDisplayOrder from '../../open-search/partials/settings-form/FacetDisplayOrder';
import { RULE_ID_FIELD, hasUsableColorRules, unsafeRulePattern } from '../helpers';
import {
  filterSourceDefaults,
  lineColor,
  colorRule,
  colorRuleCondition,
  groupDefaults,
  generatedColorSequence
} from './settings-form/default-values';

const TOOLTIP_ADVANCED_EXAMPLE = `[
  { "text": "<b>Value</b>: {{metric}} units" },
  { "when": "category", "text": "<b>Category</b>: {{category}}" },
  { "dims": ["dim_a","dim_b","dim_c"], "labels": {"dim_a":"A","dim_b":"B","dim_c":"C"}, "prefix": "<b>Config</b>: ", "join": " · " }
]`;
const TOOLTIP_ADVANCED_TEMPLATE = JSON.parse(TOOLTIP_ADVANCED_EXAMPLE);

const sourcesSchema = Yup.object().shape({
  title: Yup.string().required(`Can't be blank`),
  defaultIndex: Yup.string().required(`Can't be blank`),
  style: Yup.string()
    .oneOf(['default', 'alt'])
    .required(`Can't be blank`),
  lineStyle: Yup.string()
    .oneOf(['linear', 'monotone'])
    .required(`Can't be blank`),
  provider: Yup.string()
    .oneOf(['typesense', 'opensearch'])
    .required(`Can't be blank`),
  groups: Yup.array().of(
    Yup.object({
      label: Yup.string().required(`Can't be blank`),
      labelFields: Yup.string().required(`Can't be blank`),
      description: Yup.string().required(`Can't be blank`),
      filterBy: Yup.string().test('filterby-shape', 'OpenSearch filter_by must be a JSON array', function (value) {
        if (!value) return true;
        // this.from[1] is the top-level values object (this.from[0] is the group)
        const provider = this.from?.[1]?.value?.provider;
        if (provider !== 'opensearch') return true;
        try {
          return Array.isArray(JSON.parse(value));
        } catch {
          return false;
        }
      }),
      // A rule's patterns are compiled at render time, so a pattern that does
      // not compile — or a field name left behind by an edit to groupByFields —
      // would silently drop the whole rule on the published page. Both are
      // cheap to catch here, where the author can still see which row is wrong.
      // A wholly blank condition stays valid: it is the row a new rule is
      // seeded with, and the resolver treats it as inert.
      colorRules: Yup.array().of(
        Yup.object({
          // A rule carries all four style attributes. The resolver would happily
          // let one fall through to the next matching rule, but a half-styled
          // rule is far more often a slip than an intent, and the reader of the
          // chart cannot tell the two apart — so the editor requires the set.
          color: Yup.string().required(`Can't be blank`),
          lineStyle: Yup.string().required(`Can't be blank`),
          markerShape: Yup.string().required(`Can't be blank`),
          markerFill: Yup.string().required(`Can't be blank`),
          when: Yup.array().of(
            Yup.object({
              field: Yup.string()
                .test('rule-field-known', 'Not a group field of this group', function (value) {
                  if (!value || value === RULE_ID_FIELD) return true;
                  // this.from[0] is the condition, [1] the rule, [2] the group.
                  const groupByFields = String(this.from?.[2]?.value?.groupByFields || '')
                    .split(',').map(f => f.trim());
                  return groupByFields.includes(value);
                })
                .test('rule-field-present', 'Choose the field this pattern matches', function (value) {
                  return !!value || !this.parent?.pattern;
                }),
              pattern: Yup.string()
                .test('rule-pattern-valid', 'Not a valid regular expression', function (value) {
                  if (!value) return true;
                  try {
                    new RegExp(value, 'i');
                    return true;
                  } catch {
                    return false;
                  }
                })
                .test('rule-pattern-present', 'Add a pattern, or the condition is ignored', function (value) {
                  return !!value || !this.parent?.field;
                })
                // Refused here as well as at render time, where an imported
                // config would otherwise slip past: a pattern that nests one
                // repeat inside another can spend tens of seconds on a single
                // group id, in the reader's browser. The message names the
                // shape so the author can rewrite rather than guess.
                .test('rule-pattern-safe', function (value) {
                  const unsafe = value ? unsafeRulePattern(value) : null;
                  return unsafe ? this.createError({ message: unsafe }) : true;
                }),
            }),
          ),
        }),
      ),
    }),
  ).min(1, 'At least one group is required').max(5, '5 groups is maximum.')
});

async function DoSearch(host, apiKey, indexName, config) {
  const client = new Typesense.Client({
    nodes: [
      {host: host, port: 443, protocol: 'https'}
    ],
    apiKey: apiKey,
    connectionTimeoutSeconds: 2,
  });

  const {groupByFields} = config;
  const allCombinations = [];
  let page = 1;
  const perPage = 250;
  let hasMorePages = true;

  // Fetch all pages of grouped results
  while (hasMorePages) {
    const searchParameters = {
      q: "*",
      query_by: "*",
      group_by: config.groupByFields,
      group_limit: 1,
      per_page: perPage,
      page: page,
      filter_by: config.filterBy,
    };

    const result = await client.collections(indexName).documents().search(searchParameters);

    // Extract group keys from this page
    const pageCombinations = result.grouped_hits?.map(group => {
      return group.group_key.map(key => String(key).toLowerCase()).join("|");
    }) || [];

    allCombinations.push(...pageCombinations);

    // Check if there are more pages
    const totalGroups = result.found;
    const fetchedSoFar = page * perPage;
    hasMorePages = fetchedSoFar < totalGroups;
    page++;
  }

  return allCombinations;
}

async function DoOpenSearchSearch(host, indexName, config) {
  const fields = (config.groupByFields || '').split(',').map(f => f.trim()).filter(Boolean);
  if (fields.length === 0) return [];

  let filterClauses = [];
  if (config.filterBy) {
    try {
      const parsed = JSON.parse(config.filterBy);
      if (Array.isArray(parsed)) {
        filterClauses = parsed.map(f => ({ terms: { [f.field]: f.values } }));
      }
    } catch {
      // ignore malformed filterBy — behave like no filter
    }
  }

  const headers = {
    'Content-Type': 'application/json',
    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
  };
  const previewToken = document.getElementById('library-preview-token')?.dataset?.value;
  if (previewToken) headers['x-preview-token'] = previewToken;

  const allCombinations = [];
  let afterKey = null;

  while (true) {
    const body = {
      size: 0,
      aggs: {
        combinations: {
          composite: {
            size: 250,
            // `missing_bucket` because a composite source drops a document that
            // lacks the field, while the chart keeps it: buildData joins the
            // group fields with '|' and an absent value becomes an empty
            // segment. Without this the combinations of any row missing one
            // field never reach the palette, and their curves render with the
            // black fallback while the author sees a shorter list and no reason
            // for it.
            sources: fields.map(f => ({ [f]: { terms: { field: f, missing_bucket: true } } })),
            ...(afterKey ? { after: afterKey } : {}),
          },
        },
      },
    };

    if (filterClauses.length > 0) {
      body.query = { bool: { filter: filterClauses } };
    }

    const response = await fetch(`${host}/${indexName}/_search`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) throw new Error(`OpenSearch ${response.status} for ${indexName}`);

    const data = await response.json();
    const buckets = data.aggregations?.combinations?.buckets || [];

    for (const b of buckets) {
      // A missing bucket comes back as null; render it as the empty segment the
      // chart's own group key uses, so the two match.
      allCombinations.push(fields.map(f => (b.key[f] == null ? '' : String(b.key[f]).toLowerCase())).join('|'));
    }

    afterKey = data.aggregations?.combinations?.after_key;
    if (!afterKey || buckets.length === 0) break;
  }

  return allCombinations;
}

// A pool of colors deep enough that `onClickHandler` can shift one off per
// unique data combination without running dry. The first 100 are distinct; past
// that the sequence repeats, which is a deliberate cap — a chart with over 100
// lines has bigger legibility problems than color reuse.
function generateColors() {
  const colors = [];
  for (let i = 0; i < 5; i++) {
    for (const item of generatedColorSequence) {
      colors.push(item);
    }
  }
  return colors;
}

export default function NvChartSettingsForm({submitHandler, defaultValues}) {

  function onClickHandler(values, groupIndex, evt, form) {
    const {defaultIndex, provider} = values;

    evt.target.classList.add('opacity-75');
    evt.target.disabled = true;

    const colors = generateColors();

    // Get existing colorsMap to preserve user customizations
    const existingColorsMap = values.groups[groupIndex].colorsMap || [];
    const existingColorsById = {};
    existingColorsMap.forEach(item => {
      existingColorsById[item.id] = item;
    });

    let searchPromise;
    if (provider === 'opensearch') {
      const host = `${location.protocol}//${location.host}`;
      searchPromise = DoOpenSearchSearch(host, defaultIndex, values.groups[groupIndex]);
    } else {
      const tsHostElement = document.querySelector('meta[name="typesense-host"]');
      const tsKeyElement = document.querySelector('meta[name="typesense-key"]');
      searchPromise = DoSearch(tsHostElement.content, tsKeyElement.content, defaultIndex, values.groups[groupIndex]);
    }

    searchPromise
      .then(combinations => {
        const newColorsMap = combinations.map(c => {
          // If this ID existed before, reuse the user's customizations
          if (existingColorsById[c]) {
            return existingColorsById[c];
          }
          // Otherwise, assign a new color from the palette
          return {
            id: c,
            color: colors.shift(),
            lineStyle: 'solid',
            markerShape: 'circle',
            markerFill: 'filled',
          };
        });
        form.setFieldValue(`groups.${groupIndex}.colorsMap`, newColorsMap);
        evt.target.classList.remove('opacity-75');
      })
      .catch()
      .finally(_ => {
        evt.target.disabled = false;
        evt.target.classList.remove('opacity-75');
      });
  }

  return (
    <div className='nv-chart-settings-form'>
      <Formik initialValues={defaultValues} validationSchema={sourcesSchema} onSubmit={values => submitHandler(values)}>
        {({values}) => {
          return (
            <Form>
              <div className='nv-chart-settings-form__fields'>

                {/* Basic settings */}
                <div className='nv-chart-settings-form__section'>
                  <details>
                    <summary>
                      Basic settings
                    </summary>
                    <div>
                      <div className='nv-chart-settings-form__field'>
                        <TextField name='title' label='Title'/>
                      </div>
                      <div className='nv-chart-settings-form__field'>
                        <TextField name='defaultIndex' label='Collection ID' hint='Index or collection name'/>
                      </div>
                      <div className='nv-chart-settings-form__field'>
                        <label className='form-label' htmlFor='provider'>Provider</label>
                        <Field as='select' id='provider' name='provider' className='form-control form-control-sm'>
                          <option value='typesense'>Typesense</option>
                          <option value='opensearch'>OpenSearch</option>
                        </Field>
                        <ErrorMessage name='provider' component='p' className='field-error mt-2 mb-0'/>
                      </div>
                      <div className="row">
                        <div className="col-lg-6">
                          <div className='nv-chart-settings-form__field'>
                            <label className='form-label' htmlFor='style'>Style</label>
                            <Field as='select' id='style' name='style' className='form-control form-control-sm'>
                              <option value='default'>Default</option>
                              <option value='alt'>Alt</option>
                            </Field>
                            <ErrorMessage name='style' component='p' className='field-error mt-2 mb-0'/>
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className='nv-chart-settings-form__field'>
                            <label className='form-label' htmlFor='lineStyle'>Line Style</label>
                            <Field as='select' id='lineStyle' name='lineStyle' className='form-control form-control-sm'>
                              <option value=''>Select</option>
                              <option value='monotone'>Smooth</option>
                              <option value='linear'>Linear</option>
                            </Field>
                            <ErrorMessage name='lineStyle' component='p' className='field-error mt-2 mb-0'/>
                          </div>
                        </div>
                        <div className="col-lg-12">
                          <div className='nv-chart-settings-form__field'>
                            <Checkbox name='collapsibleFilters' label='Collapsible filter sections'/>
                          </div>
                        </div>
                      </div>
                    </div>
                  </details>
                </div>

                {/* Image footer — chart-wide, so it sits above the per-group
                    sections. The Download control that produces these images is
                    enabled per group (Group N -> Toolbar); the intro line says
                    so, since the two halves live in different places. */}
                <div className='nv-chart-settings-form__section'>
                  <details>
                    <summary>
                      Image footer
                    </summary>
                    <div>
                      <p className='nv-chart-settings-form__hint mt-1 mb-1'>
                        Stamped on chart images downloaded from any group. The page URL and
                        date are added automatically. Enable the Download control per group
                        under Toolbar.
                      </p>
                      <div className='row'>
                        <div className="col-lg-12">
                          <div className='nv-chart-settings-form__field'>
                            <TextField name='imageFooter.source' label='Source'
                              hint='Shown as "Source: <your text>".'/>
                          </div>
                        </div>
                        <div className="col-lg-12">
                          <div className='nv-chart-settings-form__field'>
                            <TextAreaField name='imageFooter.disclaimer' label='Disclaimer'
                              hint='Printed below the source line.'/>
                          </div>
                        </div>
                      </div>
                    </div>
                  </details>
                </div>

                {/* Groups */}
                <div className='nv-chart-settings-form__groups'>
                  <FieldArray name='groups'>
                    {({remove, push, form}) => {
                      return (
                        <div className='group'>
                          {values.groups && values.groups.length > 0 &&
                            values.groups.map((entry, idx) => {
                              return (
                                <div className='nv-chart-settings-form__section group-settings' key={idx}>
                                  <details>
                                    <summary>
                                      Group {idx + 1}
                                    </summary>
                                    <div>
                                      <div className='row'>
                                        <div className='col-4'>
                                          <TextField name={`groups[${idx}].label`} label='Label'/>
                                        </div>
                                        <div className='col-4'>
                                          <TextField name={`groups[${idx}].description`} label='Description'/>
                                        </div>
                                        <div className='col-4'>
                                          <DropdownField
                                            name={`groups[${idx}].type`}
                                            label='Chart Type'
                                            options={[
                                              ['line', 'Line']
                                            ]}
                                          />
                                        </div>
                                        <div className='col-4'>
                                          <TextField name={`groups[${idx}].groupByFields`} label='GroupBy fields'
                                                     placeholder='Field name' hint='Comma separated list of fields.'/>
                                        </div>
                                        <div className='col-4'>
                                          <TextField name={`groups[${idx}].groupLabelFormat`} label='Group Label Format'
                                                     placeholder='Field name' hint={`Example: {{hw}} ({{framework}}, {{precision}})`}/>
                                        </div>
                                        <div className='col-4'>
                                          <TextField name={`groups[${idx}].labelFields`} label='Chart Title'
                                                     hint='Comma separated list of fields.'/>
                                        </div>
                                      </div>

                                      <hr/>

                                      <div className='row'>
                                        <FieldArray name={`groups[${idx}].filters`}>
                                          {({push: pushAllowed, remove: removeAllowed}) => {
                                            const items = form.values.groups[idx].filters;
                                            return (
                                              <div className='group'>
                                                <h3 className='h--smallest text-white'>Filters</h3>
                                                <div className='col-12'>
                                                  <TextAreaField name={`groups[${idx}].filterBy`} label='Filter by'/>
                                                  {values.provider === 'opensearch' ? (
                                                    <p className='nv-chart-settings-form__hint mb-3'>
                                                      JSON array in Search UI filter format.<br/>
                                                      Example: <code>{`[{"field":"hw","values":["H200","H100"],"type":"any"}]`}</code>
                                                    </p>
                                                  ) : (
                                                    <p className='nv-chart-settings-form__hint mb-3'>
                                                      Typesense <code>filter_by</code> expression. See <a href="https://typesense.org/docs/26.0/api/search.html#filter-parameters" target="_blank" rel="noopener noreferrer">documentation</a>.<br/>
                                                      Example: <code>{`hw:=[H200,H100,GB200] && precision:=[FP4]`}</code>
                                                    </p>
                                                  )}
                                                </div>

                                                {items && items.length > 0 && items.map((item, gIdx) => {
                                                  const base = `groups[${idx}].filters.${gIdx}`;
                                                  return (
                                                    <div key={item.id} className='nv-chart-settings-form__filter-item'>
                                                      <div className='nv-chart-settings-form__filter-header'>
                                                        <span className='nv-chart-settings-form__filter-num'>Filter {gIdx + 1}</span>
                                                        <button type="button" className="btn btn-danger btn-sm"
                                                                aria-label='Remove filter'
                                                                onClick={() => removeAllowed(gIdx)}>
                                                          ×
                                                        </button>
                                                      </div>
                                                      <div className='row'>
                                                        <div className="col-6">
                                                          <TextField name={`${base}.label`} label='Label'/>
                                                        </div>
                                                        <div className="col-6">
                                                          <div className='nv-chart-settings-form__form-field'>
                                                          <label className='form-label'>Widget</label>
                                                          <Field as='select' name={`${base}.kind`} className='form-control form-control-sm'>
                                                            <optgroup label='Filters — narrow rows shown'>
                                                              {[...GENERIC_FILTER_KINDS, ...ROW_FILTER_KINDS].map(f => (
                                                                <option key={f.kind} value={f.kind}>{f.label}</option>
                                                              ))}
                                                            </optgroup>
                                                            <optgroup label='Axis controls — change what is plotted'>
                                                              {AXIS_FILTER_KINDS.map(f => (
                                                                <option key={f.kind} value={f.kind}>{f.label}</option>
                                                              ))}
                                                            </optgroup>
                                                          </Field>
                                                          </div>
                                                        </div>
                                                      </div>

                                                      {(() => {
                                                        // Special filter kind (grouped-dropdown / axis-switcher)
                                                        // renders its own editor; core widgets fall through to the
                                                        // generic facet editor below.
                                                        const feature = FILTER_KINDS.find(f => f.kind === item.kind);
                                                        return feature ? <feature.Editor base={base}/> : null;
                                                      })()}
                                                      {!FILTER_KINDS.some(f => f.kind === item.kind) && (
                                                        <>
                                                          <div className='row'>
                                                            <div className="col-6">
                                                              <TextField name={`${base}.attribute`} label='Attribute' placeholder='Field name'/>
                                                            </div>
                                                          </div>
                                                          <div className='row'>
                                                            <div className="col-12">
                                                              <MultiSelectField
                                                                name={`${base}.defaultValues`}
                                                                label='Default Selections'
                                                                attribute={item.attribute}
                                                                defaultIndex={values.defaultIndex}
                                                                filterBy={values.groups[idx].filterBy}
                                                                provider={values.provider}
                                                                hint={item.kind === 'checkboxes'
                                                                  ? 'Facet values that will be pre-selected when the chart loads. Leave empty to have all options unchecked by default.'
                                                                  : 'Facet value that will be pre-selected when the chart loads. Leave empty to auto-select the first value alphabetically.'}
                                                              />
                                                            </div>
                                                          </div>
                                                          {item.kind === 'checkboxes' && (
                                                            <div className='row'>
                                                              <div className="col-12">
                                                                <label className='mb-1'>Display order</label>
                                                                <FacetDisplayOrder
                                                                  field={item.attribute}
                                                                  indexes={values.defaultIndex}
                                                                  value={item.order}
                                                                  onChange={(next) => form.setFieldValue(`${base}.order`, next)}
                                                                />
                                                                <p className='nv-chart-settings-form__hint'>
                                                                  Manual value order. Unlisted values keep their default (alphabetical) position.
                                                                </p>
                                                              </div>
                                                            </div>
                                                          )}
                                                        </>
                                                      )}
                                                    </div>
                                                  );
                                                })}
                                                <button
                                                  type="button"
                                                  className="btn btn-outline-primary btn-sm"
                                                  onClick={() =>
                                                    pushAllowed({
                                                      ...filterSourceDefaults,
                                                      id: crypto.randomUUID(),
                                                    })
                                                  }
                                                >
                                                  + Add Filter
                                                </button>
                                              </div>);
                                          }}
                                        </FieldArray>
                                      </div>

                                      <hr/>

                                      {/* X Axis */}
                                      <div className='row'>
                                        <div className="col-12">
                                          <h3 className="h--smallest text-white">X Axis</h3>
                                        </div>
                                        <div className='col-6'>
                                          <TextField name={`groups[${idx}].axes.x.label`} label='Label' hint='X axis label'/>
                                        </div>
                                        <div className='col-6'>
                                          <TextField name={`groups[${idx}].axes.x.field`} label='Field' hint='Field name'/>
                                        </div>

                                        <div className="col-12">
                                          <h3 className="h--smallest text-white">Y Axis</h3>
                                        </div>
                                        <div className='col-6'>
                                          <TextField name={`groups[${idx}].axes.y.label`} label='Label' hint='Y axis label'/>
                                        </div>
                                        <div className='col-6'>
                                          <TextField name={`groups[${idx}].axes.y.field`} label='Field' hint='Field name'/>
                                        </div>

                                        {/* Additional Y metrics: selectable from the chart's Y-Axis dropdown */}
                                        <div className='col-12'>
                                          <FieldArray name={`groups[${idx}].axes.yOptions`}>
                                            {({push: pushYOption, remove: removeYOption}) => {
                                              const yItems = form.values.groups[idx].axes.yOptions || [];
                                              return (
                                                <div className='group'>
                                                  {yItems.length > 0 && (
                                                    <p className='nv-chart-settings-form__subsection-heading'>Additional Y metrics</p>
                                                  )}
                                                  {yItems.map((item, yIdx) => (
                                                    <div key={item.id ?? yIdx} className='row'>
                                                      <div className='col-6'>
                                                        <TextField name={`groups[${idx}].axes.yOptions.${yIdx}.label`} label='Label'/>
                                                      </div>
                                                      <div className='col-5'>
                                                        <TextField name={`groups[${idx}].axes.yOptions.${yIdx}.field`} label='Field'/>
                                                      </div>
                                                      <div className='col-1 align-self-end my-2'>
                                                        <button type='button' className='btn btn-danger btn-sm'
                                                                aria-label='Remove Y axis option'
                                                                onClick={() => removeYOption(yIdx)}>×</button>
                                                      </div>
                                                    </div>
                                                  ))}
                                                  <button type='button' className='btn btn-outline-primary btn-sm'
                                                          onClick={() => pushYOption({label: '', field: '', id: crypto.randomUUID()})}>
                                                    + Add Y metric
                                                  </button>
                                                  <p className='nv-chart-settings-form__hint'>
                                                    Extra Y metrics, selectable from the chart's Y-Axis dropdown. The switcher
                                                    appears only when there is more than one option (this Y Axis is the default).
                                                  </p>
                                                </div>
                                              );
                                            }}
                                          </FieldArray>
                                        </div>
                                      </div>

                                      <hr/>

                                      {/* Toolbar — opt-in per chart (off by default) */}
                                      <div className='row'>
                                        <div className="col-12">
                                          <h3 className="h--smallest text-white">Toolbar</h3>
                                        </div>
                                        <div className='col-12'>
                                          <Checkbox name={`groups[${idx}].toolbar.viewToggle`} label='Chart / Table view switcher'/>
                                        </div>
                                        <div className='col-12'>
                                          <Checkbox name={`groups[${idx}].toolbar.download`} label='Download dropdown (PNG/SVG/CSV)'/>
                                        </div>
                                        <div className='col-12'>
                                          <Checkbox name={`groups[${idx}].toolbar.share`} label='Share / copy link button'/>
                                        </div>
                                        <div className='col-12'>
                                          <p className='nv-chart-settings-form__hint'>Show or hide controls above the chart.</p>
                                        </div>
                                      </div>

                                      <hr/>

                                      {/* Tooltip */}
                                      <div className='row'>
                                        <div className="col-12">
                                          <h3 className="h--smallest text-white">Tooltip</h3>
                                        </div>
                                        <div className='col-12'>
                                          <TextField name={`groups[${idx}].tooltip.title`} label='Title'/>
                                        </div>
                                        <div className='col-12 nv-chart-settings-form__compact-textarea'>
                                          <TextAreaField name={`groups[${idx}].tooltip.text`} label='Tooltip — simple'
                                            hint='One template line, always shown. Use {{fieldname}} for values. Ignored when Advanced is set.'/>
                                        </div>
                                        <div className='col-12'>
                                          <JsonField
                                            name={`groups[${idx}].tooltip.advanced`}
                                            label='Tooltip — advanced (JSON)'
                                            rows={6}
                                            placeholder={TOOLTIP_ADVANCED_EXAMPLE}
                                            template={TOOLTIP_ADVANCED_TEMPLATE}
                                            help='Optional. When present, replaces the simple tooltip above. Each entry is a line: { "text": "..." } always shown, { "when": "field", "text": "..." } shown only when that field has a value, { "dims": [...] } renders only populated dimensions.'
                                          />
                                        </div>
                                      </div>

                                      <hr/>

                                      {/* Advanced */}
                                      <details className='nv-chart-settings-form__advanced'>
                                        <summary className='h--smallest text-white'>Advanced</summary>
                                        <p className='nv-chart-settings-form__hint mt-1 mb-1'>Optional features for specialized chart types.</p>
                                        <div className='row'>
                                          <div className='col-12'>
                                            <p className='nv-chart-settings-form__subsection-heading'>X axis band</p>
                                          </div>
                                          <div className='col-4'>
                                            <DropdownField
                                              name={`groups[${idx}].axes.x.band.mode`}
                                              label='Band'
                                              options={[
                                                ['', 'Off'],
                                                ['lines', 'Lines (two edges)'],
                                                ['area', 'Area (shaded fill)'],
                                              ]}
                                            />
                                          </div>
                                          <div className='col-2'>
                                            <TextField name={`groups[${idx}].axes.x.band.fields.0`} label='Low' placeholder='p75_field'/>
                                          </div>
                                          <div className='col-2'>
                                            <TextField name={`groups[${idx}].axes.x.band.fields.1`} label='High' placeholder='p90_field'/>
                                          </div>
                                          <div className='col-4'>
                                            <TextField name={`groups[${idx}].axes.x.band.label`} label='Band label' placeholder='(optional)'/>
                                          </div>
                                          <div className='col-4'>
                                            <TextField name={`groups[${idx}].axes.x.band.showWhenField`}
                                              label='Only show when rows have' placeholder='(field name)'
                                              hint='Optional. Hides the band option unless the rows in the current selection carry this field. Empty: always offered.'/>
                                          </div>
                                          <div className='col-12'>
                                            <p className='nv-chart-settings-form__hint'>
                                              Draws a shaded region between the two configured fields. <b>Lines</b>: two
                                              edge lines, high edge dashed. <b>Area</b>: shaded fill between them. Requires
                                              an Axis metric switcher — adds a band option to it automatically.
                                            </p>
                                          </div>
                                        </div>
                                        <hr className='my-2'/>
                                        <div className='row'>
                                          <div className='col-12'>
                                            <p className='nv-chart-settings-form__subsection-heading'>Pareto frontier</p>
                                          </div>
                                          <div className='col-12'>
                                            <Checkbox name={`groups[${idx}].pareto.frontierOnly`} label='Draw only the Pareto frontier'/>
                                          </div>
                                          <div className='col-12'>
                                            <p className='nv-chart-settings-form__hint'>
                                              Per curve, connects only the non-dominated points into a clean monotonic
                                              line. For Pareto benchmark charts — leave off for ordinary line charts.
                                            </p>
                                          </div>
                                          {/* Only meaningful once the frontier is drawn: without it every point is
                                              already on the chart, so there is no dominated set to distinguish.
                                              Disabled rather than hidden so the option stays discoverable. */}
                                          <div className='col-12'>
                                            <Checkbox name={`groups[${idx}].pareto.showDominated`}
                                              label='Show dominated points as hollow markers'
                                              disabled={!values.groups[idx].pareto?.frontierOnly}/>
                                          </div>
                                          <div className='col-12'>
                                            <p className='nv-chart-settings-form__hint'>
                                              Off-frontier points are hidden unless this is on. Requires the option above.
                                            </p>
                                          </div>
                                        </div>
                                      </details>

                                      <hr/>

                                      <div className='row'>
                                        <div className='col-12'>
                                          <h3 className="h--smallest text-white">Line colors</h3>
                                        </div>

                                        {/* Rules. Each row matches the group's fields positionally, so
                                            one row can style a whole hardware family instead of the family
                                            being restated on every combination under Advanced. */}
                                        <FieldArray name={`groups[${idx}].colorRules`}>
                                          {({push: pushRule, remove: removeRule, move: moveRule}) => {
                                            const rules = form.values.groups[idx].colorRules || [];
                                            // `$id` last: it is the escape hatch for a pattern that spans
                                            // fields, not one of the group's own fields.
                                            const fieldOptions = [
                                              ['', 'Field…'],
                                              ...String(form.values.groups[idx].groupByFields || '')
                                                .split(',').map(f => f.trim()).filter(Boolean).map(f => [f, f]),
                                              [RULE_ID_FIELD, `${RULE_ID_FIELD} — whole group key`],
                                            ];
                                            return (
                                              <div className='col-12'>
                                                <div className='d-flex justify-content-between align-items-baseline'>
                                                  <p className='nv-chart-settings-form__subsection-heading'>Rules</p>
                                                  <button type='button' className='btn btn-outline-primary btn-sm'
                                                          onClick={() => pushRule({
                                                            ...colorRule,
                                                            id: crypto.randomUUID(),
                                                            when: [{...colorRuleCondition}],
                                                          })}>
                                                    + rule
                                                  </button>
                                                </div>
                                                <p className='nv-chart-settings-form__hint mb-2'>
                                                  Style lines by what their group fields contain. A rule sets the
                                                  color, line style, marker shape and fill for every line matching
                                                  all of its conditions. Rules are checked top to bottom and the
                                                  first match wins; any line no rule matches falls back to the
                                                  Advanced table below. Patterns are regular expressions —
                                                  case-insensitive, and matching anywhere in the value unless you
                                                  anchor them with ^ (write {'^h\\d00'}, not h).
                                                </p>

                                                {rules.map((rule, rIdx) => (
                                                  <div key={rule.id || rIdx} className='my-2'>
                                                    <div className='row'>
                                                      {/* Order is the precedence mechanism, so it has to be
                                                          editable; ↑/↓ rather than dragging, matching
                                                          FacetDisplayOrder's keyboard-reachable controls. */}
                                                      <div className='col-1 align-self-end my-2 d-flex'>
                                                        <button type='button' className='btn btn-outline-secondary btn-sm'
                                                                title='Move up' aria-label='Move rule up' disabled={rIdx === 0}
                                                                onClick={() => moveRule(rIdx, rIdx - 1)}>↑
                                                        </button>
                                                        <button type='button' className='btn btn-outline-secondary btn-sm'
                                                                title='Move down' aria-label='Move rule down'
                                                                disabled={rIdx === rules.length - 1}
                                                                onClick={() => moveRule(rIdx, rIdx + 1)}>↓
                                                        </button>
                                                      </div>
                                                      <div className='col-3'>
                                                        <TextField name={`groups[${idx}].colorRules.${rIdx}.label`} label=''
                                                                   placeholder='Label (e.g. Hopper)' classes={['d-flex flex-row']}/>
                                                      </div>
                                                      <div className='col-3 color-field'>
                                                        <TextField name={`groups[${idx}].colorRules.${rIdx}.color`}
                                                                   label='' placeholder='Color'
                                                                   classes={['d-flex flex-row']}/>
                                                        <ColorField name={`groups[${idx}].colorRules.${rIdx}.color`}
                                                                    label='' placeholder='Color'
                                                                    showError={false}
                                                                    classes={['d-flex flex-row']}/>
                                                      </div>
                                                      {/* Same option values as an Advanced row — a rule sets all
                                                          four, so there is no "inherit" choice to make here. */}
                                                      <div className='col-4 d-flex line-and-dots'>
                                                        <DropdownField
                                                          name={`groups[${idx}].colorRules.${rIdx}.lineStyle`}
                                                          label=''
                                                          options={[
                                                            ['solid', 'Solid line'],
                                                            ['dashed', 'Dashed line'],
                                                            ['dotted', 'Dotted line'],
                                                          ]}
                                                        />
                                                        <DropdownField
                                                          name={`groups[${idx}].colorRules.${rIdx}.markerShape`}
                                                          label=''
                                                          options={[
                                                            ['circle', 'Circle'],
                                                            ['square', 'Square'],
                                                            ['triangle', 'Triangle'],
                                                            ['diamond', 'Diamond'],
                                                            ['cross', 'Cross'],
                                                          ]}
                                                        />
                                                        <DropdownField
                                                          name={`groups[${idx}].colorRules.${rIdx}.markerFill`}
                                                          label=''
                                                          options={[
                                                            ['filled', 'Filled'],
                                                            ['empty', 'Empty'],
                                                          ]}
                                                        />
                                                      </div>
                                                      <div className='col-1 align-self-end my-2'>
                                                        <button type='button' className='btn btn-danger btn-sm'
                                                                aria-label='Remove rule'
                                                                onClick={() => removeRule(rIdx)}>
                                                          ×
                                                        </button>
                                                      </div>
                                                    </div>

                                                    <FieldArray name={`groups[${idx}].colorRules.${rIdx}.when`}>
                                                      {({push: pushCondition, remove: removeCondition}) => (
                                                        <div>
                                                          {(rule.when || []).map((condition, cIdx) => (
                                                            // Keyed by position, not by the chosen field: the
                                                            // conditions are an ordered array precisely so two of
                                                            // them can name the same field, and keying on the name
                                                            // would relocate the row — remounting its inputs
                                                            // mid-edit — the moment the dropdown changes.
                                                            <div key={cIdx} className='row'>
                                                              <div className='col-1 align-self-center'>
                                                                <p className='nv-chart-settings-form__hint mb-0'>{cIdx === 0 ? 'when' : 'and'}</p>
                                                              </div>
                                                              <div className='col-4'>
                                                                <DropdownField
                                                                  name={`groups[${idx}].colorRules.${rIdx}.when.${cIdx}.field`}
                                                                  label=''
                                                                  options={fieldOptions}
                                                                />
                                                              </div>
                                                              <div className='col-6'>
                                                                <TextField
                                                                  name={`groups[${idx}].colorRules.${rIdx}.when.${cIdx}.pattern`}
                                                                  label='' placeholder='^h\d00'
                                                                  classes={['d-flex flex-row']}/>
                                                              </div>
                                                              <div className='col-1 align-self-end my-2'>
                                                                <button type='button' className='btn btn-danger btn-sm'
                                                                        aria-label='Remove condition'
                                                                        onClick={() => removeCondition(cIdx)}>
                                                                  ×
                                                                </button>
                                                              </div>
                                                            </div>
                                                          ))}
                                                          <button type='button' className='btn btn-outline-primary btn-sm'
                                                                  onClick={() => pushCondition({...colorRuleCondition})}>
                                                            + and condition
                                                          </button>
                                                        </div>
                                                      )}
                                                    </FieldArray>
                                                  </div>
                                                ))}
                                              </div>
                                            );
                                          }}
                                        </FieldArray>
                                      </div>

                                      {/* Advanced — the literal per-line table. Kept, and consulted for
                                          every line the rules above do not match, so adding rules to an
                                          existing chart is incremental and reversible. */}
                                      <details className='nv-chart-settings-form__advanced'>
                                        {/* The count and the "fallback only" state have to be legible while this
                                            is still shut: an author who opens it, edits a color and saves would
                                            otherwise see nothing change, because a rule already claimed that line.
                                            hasUsableColorRules is the resolver's own test, so the summary cannot
                                            claim a state the chart disagrees with. */}
                                        <summary className='h--smallest text-white'>
                                          {`Advanced — ${(form.values.groups[idx].colorsMap || []).length} individual lines${hasUsableColorRules(form.values.groups[idx]) ? ' · fallback only' : ''}`}
                                        </summary>
                                        <p className='nv-chart-settings-form__hint mt-1 mb-1'>
                                          One row per exact group ID. While Rules are set above, these apply only to
                                          lines no rule matched. Generate replaces every row.
                                        </p>
                                        <div className='row'>
                                          <div className='col-12'>
                                            <button className='btn btn-outline-warning' type='button'
                                                    onClick={(evt) => onClickHandler(values, idx, evt, form)}>Generate
                                            </button>
                                            <p className='nv-chart-settings-form__hint mt-1'>This action cannot be undone. After
                                              generation, all colors will be overwritten.</p>
                                          </div>
                                          <FieldArray name={`groups[${idx}].colorsMap`}>
                                            {({push: pushLineColor, remove: removeLineColor}) => {
                                              const items = form.values.groups[idx].colorsMap;
                                              return (
                                                <div className='group'>
                                                  {items && items.length > 0 && items.map((item, gIdx) => {
                                                    return (
                                                      <div key={item.id} className='row'>
                                                        <div className="col-4">
                                                          <TextField name={`groups[${idx}].colorsMap.${gIdx}.id`} label=''
                                                                     placeholder='ID' classes={['d-flex flex-row']}/>
                                                        </div>
                                                        <div className="col-3 color-field">
                                                          <TextField name={`groups[${idx}].colorsMap.${gIdx}.color`}
                                                                     label='' placeholder='Color'
                                                                     classes={['d-flex flex-row']}/>
                                                          <ColorField name={`groups[${idx}].colorsMap.${gIdx}.color`}
                                                                      label='' placeholder='Color'
                                                                      classes={['d-flex flex-row']}/>
                                                        </div>
                                                        <div className='col-4 d-flex line-and-dots'>
                                                          <DropdownField
                                                            name={`groups[${idx}].colorsMap.${gIdx}.lineStyle`}
                                                            label=''
                                                            options={[
                                                              ['solid', 'Solid line'],
                                                              ['dashed', 'Dashed line'],
                                                              ['dotted', 'Dotted line'],
                                                            ]}
                                                          />
                                                          <DropdownField
                                                            name={`groups[${idx}].colorsMap.${gIdx}.markerShape`}
                                                            label=''
                                                            options={[
                                                              ['circle', 'Circle'],
                                                              ['square', 'Square'],
                                                              ['triangle', 'Triangle'],
                                                              ['diamond', 'Diamond'],
                                                              ['cross', 'Cross'],
                                                            ]}
                                                          />
                                                          <DropdownField
                                                            name={`groups[${idx}].colorsMap.${gIdx}.markerFill`}
                                                            label=''
                                                            options={[
                                                              ['filled', 'Filled'],
                                                              ['empty', 'Empty'],
                                                            ]}
                                                          />
                                                        </div>

                                                        <div className="col-1 align-self-end my-2">
                                                          <button type="button" className="btn btn-danger btn-sm"
                                                                  aria-label='Remove line color'
                                                                  onClick={() => removeLineColor(gIdx)}>
                                                            ×
                                                          </button>
                                                        </div>
                                                      </div>
                                                    );
                                                  })}
                                                  <button
                                                    type="button"
                                                    className="btn btn-outline-primary btn-sm"
                                                    onClick={() =>
                                                      pushLineColor({
                                                        ...lineColor,
                                                        id: crypto.randomUUID(),
                                                      })
                                                    }
                                                  >
                                                    + Add Color
                                                  </button>
                                                </div>);
                                            }}
                                          </FieldArray>
                                        </div>
                                      </details>

                                      <div className="row">
                                        <div className='col-12 my-2 pt-4'>
                                          <button type='button' className='btn btn-outline-warning btn-sm'
                                                  onClick={() => remove(idx)}>
                                            Remove Group
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </details>
                                </div>
                              );
                            })}
                          <button type='button' className='btn btn-outline-light btn-sm'
                                  onClick={() => push({...groupDefaults})}>
                            Add Group
                          </button>
                        </div>
                      );
                    }}
                  </FieldArray>
                </div>
              </div>
              <div className='nv-chart-settings-form__actions'>
                <button type='submit' className='btn btn-info btn-sm'>Save settings</button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}
